import {
  Button,
  Heading,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useState } from 'react';
import {
  HeadingSquare,
  LinkToCSV,
} from 'src/components/AddNewPeopleModal/AddNewPeopleModal.utils';
import { EmployeesToAdd } from 'src/components/AddNewPeopleModal/EmployeesToAdd';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { EmployeeForm } from 'src/components/EmployeeForm/EmployeeForm';
import { Employee } from 'src/components/EmployeeForm/EmployeeForm.utils';
import { Modal } from 'src/components/Modal/Modal';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';

export interface IAddNewPeopleModal {
  onSuccess: (employeeCount: number) => void;
  corpId: string;
  onCancel: () => void;
  onShowCSVModal: () => void;
  closeIcon: JSX.Element;
  onClose: () => void;
}

export function AddNewPeopleModal({
  corpId,
  onCancel,
  onSuccess,
  onShowCSVModal,
  closeIcon,
  onClose,
}: IAddNewPeopleModal): JSX.Element {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const hasUnsavedEmployees = employees.length > 0;
  const { sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    sendTealiumEvent({
      eventcategory: 'self-service management tool_people',
      eventaction: 'add people to account_impression',
      non_interaction: '1',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddEmployee = (employee: Employee) => {
    setEmployees([...employees, employee]);
  };

  const onRemoveEmployee = (email: string) => {
    const filteredEmployees = employees.filter(
      (employee) => email !== employee.email
    );
    setEmployees(filteredEmployees);
  };

  const onUpload = (employeeCount: number) => {
    onSuccess(employeeCount);
  };

  const emailValidation = (email: keyof Employee) => {
    const isValid = employees.every((employee) => employee.email !== email);
    return isValid || 'Email is already added below';
  };

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <ConfirmationDialog enabled={hasUnsavedEmployees}>
          {(onConfirm) => (
            <Button
              variant="tertiary"
              type="button"
              className="closeModalButton"
              onClick={() => onConfirm(onClose)}
            >
              {closeIcon}
            </Button>
          )}
        </ConfirmationDialog>
        <HeadingSquare inset="md">
          <Heading variant="h1">Add new people</Heading>
        </HeadingSquare>
        <Square inset="md">
          Add people manually or batch import people by{' '}
          <ConfirmationDialog enabled={hasUnsavedEmployees}>
            {(onConfirm) => (
              <Stack as="span" onClick={() => onConfirm(onShowCSVModal)}>
                <LinkToCSV variant="body" color="interactive">
                  uploading a CSV
                </LinkToCSV>
              </Stack>
            )}
          </ConfirmationDialog>
        </Square>
        <Square inset="md">
          <EmployeeForm
            corpId={corpId}
            emailValidation={emailValidation}
            onAdd={onAddEmployee}
          />
        </Square>
      </Square>
      <ConfirmationDialog enabled={hasUnsavedEmployees}>
        {(onConfirm) => (
          <EmployeesToAdd
            corpId={corpId}
            onCancel={() => onConfirm(onCancel)}
            onUpload={onUpload}
            onRemove={onRemoveEmployee}
            employees={employees}
          />
        )}
      </ConfirmationDialog>
    </Modal>
  );
}

interface IConfirmationDialogProps {
  enabled: boolean;
  children: (
    confirm: (confirmCallback: () => void) => void
  ) => JSX.Element | JSX.Element[];
}

function ConfirmationDialog({
  enabled,
  children,
}: IConfirmationDialogProps): JSX.Element {
  const [show, setShow] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState(null);

  function handleCancel() {
    setShow(false);
  }

  function handleConfirm() {
    setShow(false);
    confirmCallback();
  }

  function setupConfirmAction(confirmCallback: () => void) {
    if (enabled) {
      setShow(true);
      setConfirmCallback(() => confirmCallback);
      return;
    }

    confirmCallback();
  }

  return (
    <>
      {show && (
        <SimpleDialog
          title="Leave this screen?"
          description="This will remove all the new people you plan to add"
          primaryActionText="Leave screen"
          buttonsDirection="row-reverse"
          secondaryActionText="Continue adding"
          onPrimaryActionClick={handleConfirm}
          onSecondaryActionClick={handleCancel}
        />
      )}
      {children(setupConfirmAction)}
    </>
  );
}
